import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input, Spinner } from '@elbklar/ui';
import type { SearchResult } from '@/lib/api';
import { SuchTreffer, suchZiel, useGlobaleSuche } from './Suche';

/** Globale Suche im Seitenkopf am Rechner (⌘K), Treffer als Klappliste. */
export function SearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { aktiv, treffer: results, laedt: loading } = useGlobaleSuche(query);

  const handleSelectResult = (result: SearchResult) => {
    const ziel = suchZiel(result);
    if (ziel) navigate(ziel);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelectResult(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div ref={containerRef} className="relative w-56">
      <Input
        ref={inputRef}
        icon={Search}
        size="sm"
        kbd={loading ? undefined : '⌘K'}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSelectedIndex(-1);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setOpen(true)}
        placeholder="Suchen"
        aria-label="Kunden, Partner und Kontakte suchen"
      />
      {loading && <Spinner size={14} className="absolute right-2 top-1/2 -translate-y-1/2" />}

      {open && aktiv && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-80 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md animate-ek-pop">
          {results.length > 0 ? (
            <SuchTreffer treffer={results} onWahl={handleSelectResult} ausgewaehlt={selectedIndex} />
          ) : !loading ? (
            <div className="px-4 py-6 text-center">
              <p className="t-small text-muted-foreground">Keine Treffer für „{query}“</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
